import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import React, { ReactNode, useEffect, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import { MediaSwiperFormat } from '@models/pages';
import { SwiperDefaultStyles } from '@styles/components';
import ISwiperStyles from './swiper.styles.interface';

interface Props {
  itemsArray: Array<ReactNode>;
  customStyles?: ISwiperStyles;
  id: string;
  scrollSizeLarge?: number,
  scrollSizeSmall?: number,
  format?: string,
}
function Swiper({
  itemsArray,
  customStyles,
  id,
  format,
  scrollSizeLarge,
  scrollSizeSmall,
} : Props) {
  const [showScrollButtonLeft, setShowScrollButtonLeft] = useState(false);
  const [showScrollButtonRight, setShowScrollButtonRight] = useState(false);

  const paddleStyleLeft = {
    display: showScrollButtonLeft ? 'flex' : 'none',
  };

  const paddleStyleRight = {
    display: showScrollButtonRight ? 'flex' : 'none',
  };

  const updateScrollPaddles = () => {
    const el = document.querySelector(`#listScroll_${id}`) as HTMLElement;
    if (!el) return;
    setShowScrollButtonLeft(() => {
      const isScrollable = el.scrollWidth > el!.clientWidth;
      return isScrollable && el!.scrollLeft > 0;
    });
    setShowScrollButtonRight(() => {
      const isScrollable = el.scrollWidth > el!.clientWidth;
      return isScrollable && Math.ceil(el!.scrollLeft) + el!.clientWidth < el!.scrollWidth;
    });
  };

  const isMobile = useMediaQuery({
    query: '(max-width: 767px)',
  });

  const updateWindowSize = () => {
    updateScrollPaddles();
  };

  const scrollRight = () => {
    const el = document.querySelector(`#listScroll_${id}`) as HTMLElement;
    el!.scrollBy({
      top: 0,
      left: isMobile ? scrollSizeSmall : scrollSizeLarge,
      behavior: 'smooth',
    });
  };

  const scrollLeft = () => {
    const el = document.querySelector(`#listScroll_${id}`) as HTMLElement;
    el!.scrollBy({
      top: 0,
      left: isMobile ? -scrollSizeSmall : -scrollSizeLarge,
      behavior: 'smooth',
    });
  };

  const componentDidMount = () => {
    updateWindowSize();
    window.addEventListener('resize', updateWindowSize);
  };

  const componentWillUnmount = () => {
    window.removeEventListener('resize', updateWindowSize);
  };

  useEffect(() => {
    componentDidMount();
    return () => {
      componentWillUnmount();
    };
  }, []);

  const Styles = customStyles ?? SwiperDefaultStyles;

  return (
    <Styles.SwiperSection>
      <Styles.SwiperListWrapper>
        <Styles.SwiperList id={`listScroll_${id}`} onScroll={updateScrollPaddles}>
          {itemsArray}
        </Styles.SwiperList>
        {format === MediaSwiperFormat.Headshot ? (
          <Styles.SwiperPaddleHeadshotLeft className="group" onClick={scrollLeft} aria-hidden="true" style={paddleStyleLeft}>
            <Styles.IconPaddleWrapper>
              <ChevronLeftIcon className="w-10 h-10" />
            </Styles.IconPaddleWrapper>
          </Styles.SwiperPaddleHeadshotLeft>
        )
          : (
            <Styles.SwiperPaddleLeft className="group" onClick={scrollLeft} aria-hidden="true" style={paddleStyleLeft}>
              <Styles.IconPaddleWrapper>
                <ChevronLeftIcon className="w-10 h-10" />
              </Styles.IconPaddleWrapper>
            </Styles.SwiperPaddleLeft>
          )}
        {format === MediaSwiperFormat.Headshot ? (
          <Styles.SwiperPaddleHeadshotRight className="group" onClick={scrollRight} aria-hidden="true" style={paddleStyleRight}>
            <Styles.IconPaddleWrapper>
              <ChevronRightIcon className="w-10 h-10" />
            </Styles.IconPaddleWrapper>
          </Styles.SwiperPaddleHeadshotRight>
        ) : (
          <Styles.SwiperPaddleRight className="group" onClick={scrollRight} aria-hidden="true" style={paddleStyleRight}>
            <Styles.IconPaddleWrapper>
              <ChevronRightIcon className="w-10 h-10" />
            </Styles.IconPaddleWrapper>
          </Styles.SwiperPaddleRight>
        )}
      </Styles.SwiperListWrapper>
    </Styles.SwiperSection>
  );
}

Swiper.defaultProps = {
  customStyles: null,
  scrollSizeLarge: 800,
  scrollSizeSmall: 300,
  format: MediaSwiperFormat.Standard,
};

export default Swiper;
